import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, User2, Mail, Briefcase } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { k: "mon", label: "Lunes" },
  { k: "tue", label: "Martes" },
  { k: "wed", label: "Miércoles" },
  { k: "thu", label: "Jueves" },
  { k: "fri", label: "Viernes" },
  { k: "sat", label: "Sábado" },
  { k: "sun", label: "Domingo" },
];

const DEFAULT_SCHEDULE = DAYS.reduce((acc, d) => {
  acc[d.k] = { enabled: !["sat", "sun"].includes(d.k), start: "09:00", end: "18:00" };
  return acc;
}, {});

const empty = {
  name: "", title: "", bio: "", photo_url: "",
  email: "", password: "",
  service_ids: [],
  schedule: { ...DEFAULT_SCHEDULE },
  lunch_start: "13:00",
  lunch_end: "14:00",
  time_off: [],
  commissions: {},
  active: true,
};

export default function AdminStaff() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  async function load() {
    const [s, svc] = await Promise.all([api.get("/staff"), api.get("/services")]);
    setItems(s.data);
    setServices(svc.data);
  }
  useEffect(() => { load(); }, []);

  const servicesById = useMemo(() => Object.fromEntries(services.map((x) => [x.id, x])), [services]);

  function openCreate() {
    setEditing(null);
    setForm({ ...empty, schedule: JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)) });
    setOpen(true);
  }
  function openEdit(s) {
    setEditing(s);
    setForm({
      ...s,
      password: "",
      schedule: { ...DEFAULT_SCHEDULE, ...(s.schedule || {}) },
      lunch_start: s.lunch_start || "",
      lunch_end: s.lunch_end || "",
      time_off: Array.isArray(s.time_off) ? [...s.time_off] : [],
      commissions: s.commissions || {},
    });
    setOpen(true);
  }

  function toggleService(svcId) {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(svcId)
        ? f.service_ids.filter((x) => x !== svcId)
        : [...f.service_ids, svcId],
    }));
  }

  function setDay(k, patch) {
    setForm((f) => ({ ...f, schedule: { ...f.schedule, [k]: { ...f.schedule[k], ...patch } } }));
  }

  async function save() {
    try {
      const payload = {
        name: form.name,
        title: form.title,
        bio: form.bio,
        photo_url: form.photo_url,
        service_ids: form.service_ids,
        schedule: form.schedule,
        lunch_start: (form.lunch_start || "").trim(),
        lunch_end: (form.lunch_end || "").trim(),
        time_off: (form.time_off || []).filter((t) => t.start_date && t.end_date),
        commissions: Object.fromEntries(
          Object.entries(form.commissions || {}).filter(([sid, c]) => form.service_ids.includes(sid) && c?.mode && c.mode !== "none")
        ),
        active: form.active,
      };
      if (!editing) {
        if (form.email) {
          payload.email = form.email;
          payload.password = form.password;
        }
      } else if (form.password) {
        payload.password = form.password;
      }
      if (editing) await api.put(`/staff/${editing.id}`, payload);
      else await api.post("/staff", payload);
      toast.success("Guardado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  }

  async function remove(id) {
    if (!confirm("¿Eliminar profesional?")) return;
    await api.delete(`/staff/${id}`);
    toast.success("Eliminado");
    load();
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Equipo</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Profesionales</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="staff-new-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo profesional</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar profesional" : "Nuevo profesional"}</DialogTitle></DialogHeader>

            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
                </div>
                <div className="space-y-2"><Label>Título / cargo</Label>
                  <Input placeholder="Dra. / Estilista / Abogado" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="staff-title-input" />
                </div>
              </div>
              <div className="space-y-2"><Label>Bio breve</Label>
                <Textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="staff-bio-input" />
              </div>
              <div className="space-y-2"><Label>URL foto (opcional)</Label>
                <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="staff-photo-input" />
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-sm font-medium flex items-center gap-2"><Mail className="h-4 w-4 text-[hsl(var(--accent))]" /> Acceso del profesional</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-2"><Label className="text-xs">Correo {editing && "(no editable aquí)"}</Label>
                    <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="staff-email-input" />
                  </div>
                  <div className="space-y-2"><Label className="text-xs">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
                    <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" />
                  </div>
                </div>
                <p className="text-xs text-slate-500">{editing ? "Deja vacío si no quieres cambiarla." : "Si dejas vacíos, el profesional no tendrá login propio (solo perfil)."}</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2"><Briefcase className="h-4 w-4 text-[hsl(var(--accent))]" /> Servicios que ofrece</p>
                <div className="grid sm:grid-cols-2 gap-2 max-h-44 overflow-auto p-3 rounded-md border border-slate-200 bg-white">
                  {services.length === 0 && <p className="text-xs text-slate-500">No hay servicios disponibles</p>}
                  {services.map((svc) => (
                    <label key={svc.id} className="flex items-start gap-2 cursor-pointer text-sm">
                      <Checkbox
                        checked={form.service_ids.includes(svc.id)}
                        onCheckedChange={() => toggleService(svc.id)}
                        data-testid={`staff-service-${svc.id}`}
                      />
                      <div className="leading-tight">
                        <p>{svc.name}</p>
                        <p className="text-xs text-slate-500">{svc.duration_min} min · ${svc.price.toFixed(2)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Horario semanal</p>
                <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
                  {DAYS.map((d) => {
                    const cfg = form.schedule[d.k];
                    return (
                      <div key={d.k} className="grid grid-cols-[100px_60px_1fr_8px_1fr] items-center gap-3 text-sm">
                        <span className="text-slate-700">{d.label}</span>
                        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setDay(d.k, { enabled: v })} data-testid={`staff-day-${d.k}`} />
                        <Input type="time" value={cfg.start} onChange={(e) => setDay(d.k, { start: e.target.value })} disabled={!cfg.enabled} data-testid={`staff-start-${d.k}`} />
                        <span className="text-slate-400 text-center">–</span>
                        <Input type="time" value={cfg.end} onChange={(e) => setDay(d.k, { end: e.target.value })} disabled={!cfg.enabled} data-testid={`staff-end-${d.k}`} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Hora de comida <span className="text-xs text-slate-500 font-normal">(se aplica a todos los días habilitados)</span></p>
                <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-3 rounded-md border border-slate-200 bg-white p-4 text-sm">
                  <div className="space-y-1">
                    <Label className="text-xs">Inicio</Label>
                    <Input type="time" value={form.lunch_start} onChange={(e) => setForm({ ...form, lunch_start: e.target.value })} data-testid="staff-lunch-start" />
                  </div>
                  <span className="text-slate-400 mt-5">–</span>
                  <div className="space-y-1">
                    <Label className="text-xs">Fin</Label>
                    <Input type="time" value={form.lunch_end} onChange={(e) => setForm({ ...form, lunch_end: e.target.value })} data-testid="staff-lunch-end" />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="mt-5" onClick={() => setForm({ ...form, lunch_start: "", lunch_end: "" })} data-testid="staff-lunch-clear">Sin comida</Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Vacaciones / faltas</p>
                <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
                  {(form.time_off || []).length === 0 && <p className="text-xs text-slate-500">Sin períodos bloqueados. Agrega vacaciones, faltas o eventos.</p>}
                  {(form.time_off || []).map((t, i) => (
                    <div key={t._uid || `${t.start_date}-${t.end_date}-${i}`} className="grid grid-cols-[1fr_1fr_2fr_auto] items-end gap-2 text-sm" data-testid={`staff-timeoff-row-${i}`}>
                      <div>
                        <Label className="text-xs">Desde</Label>
                        <Input type="date" value={t.start_date} onChange={(e) => {
                          const arr = [...form.time_off]; arr[i] = { ...arr[i], start_date: e.target.value };
                          setForm({ ...form, time_off: arr });
                        }} data-testid={`staff-timeoff-start-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Hasta</Label>
                        <Input type="date" value={t.end_date} onChange={(e) => {
                          const arr = [...form.time_off]; arr[i] = { ...arr[i], end_date: e.target.value };
                          setForm({ ...form, time_off: arr });
                        }} data-testid={`staff-timeoff-end-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Motivo (opcional)</Label>
                        <Input placeholder="Vacaciones / Capacitación / Personal" value={t.reason || ""} onChange={(e) => {
                          const arr = [...form.time_off]; arr[i] = { ...arr[i], reason: e.target.value };
                          setForm({ ...form, time_off: arr });
                        }} data-testid={`staff-timeoff-reason-${i}`} />
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        setForm({ ...form, time_off: form.time_off.filter((_, idx) => idx !== i) });
                      }} data-testid={`staff-timeoff-remove-${i}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({
                    ...form,
                    time_off: [...(form.time_off || []), { _uid: crypto.randomUUID(), start_date: "", end_date: "", reason: "" }],
                  })} data-testid="staff-timeoff-add">
                    <Plus className="h-3 w-3 mr-1" /> Añadir período
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-1">Comisiones por servicio <span className="text-xs text-slate-500 font-normal">(opcional)</span></p>
                <p className="text-xs text-slate-500 mb-3">Se aplica solo a los servicios que ofrece este profesional. El valor vigente al completar una cita se guarda con esa cita — cambiarlo después <strong>no afecta</strong> citas pasadas.</p>
                <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
                  {form.service_ids.length === 0 && (
                    <p className="text-xs text-slate-500 italic">Asigna primero servicios al profesional arriba.</p>
                  )}
                  {form.service_ids.map((sid) => {
                    const svc = services.find((x) => x.id === sid);
                    if (!svc) return null;
                    const cfg = form.commissions?.[sid] || { mode: "none", value: 0 };
                    const previewAmount = cfg.mode === "percent"
                      ? (svc.price * (cfg.value || 0) / 100).toFixed(2)
                      : cfg.mode === "fixed" ? Number(cfg.value || 0).toFixed(2) : "0.00";
                    return (
                      <div key={sid} className="grid grid-cols-[1.4fr_auto_1fr_1fr_auto] items-center gap-2 text-sm" data-testid={`commission-row-${sid}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{svc.name}</p>
                          <p className="text-[11px] text-slate-500">Precio ${svc.price}</p>
                        </div>
                        <span className="text-slate-400 text-xs">→</span>
                        <select
                          value={cfg.mode}
                          onChange={(e) => setForm({
                            ...form,
                            commissions: { ...form.commissions, [sid]: { ...cfg, mode: e.target.value } },
                          })}
                          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                          data-testid={`commission-mode-${sid}`}
                        >
                          <option value="none">Sin comisión</option>
                          <option value="percent">% del servicio</option>
                          <option value="fixed">Monto fijo ($)</option>
                        </select>
                        <Input
                          type="number" min="0" step="0.01"
                          disabled={cfg.mode === "none"}
                          value={cfg.value || ""}
                          placeholder={cfg.mode === "percent" ? "% (ej. 30)" : cfg.mode === "fixed" ? "Monto" : "—"}
                          onChange={(e) => setForm({
                            ...form,
                            commissions: { ...form.commissions, [sid]: { ...cfg, value: parseFloat(e.target.value) || 0 } },
                          })}
                          data-testid={`commission-value-${sid}`}
                        />
                        <span className="text-xs text-slate-500 w-20 text-right" data-testid={`commission-preview-${sid}`}>
                          {cfg.mode === "none" ? "—" : <>= <strong className="text-[#1E3A8A]">${previewAmount}</strong></>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="staff-save-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-grid">
        {items.length === 0 ? (
          <Card className="border-slate-200 sm:col-span-2 lg:col-span-3">
            <CardContent className="p-10 text-center text-slate-500">
              <User2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p>Aún no tienes profesionales. Agrega el primero para empezar.</p>
            </CardContent>
          </Card>
        ) : items.map((s) => (
          <Card key={s.id} className="border-slate-200 card-hover" data-testid={`staff-card-${s.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {s.photo_url ? (
                  <img src={s.photo_url} alt="" className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-secondary text-secondary-foreground grid place-items-center font-display text-xl">
                    {s.name?.[0] || "·"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg leading-tight truncate">{s.name}</h3>
                  <p className="text-xs text-slate-500 truncate">{s.title}</p>
                  {s.email && <p className="text-[11px] text-[hsl(var(--accent))] mt-1 truncate">{s.email}</p>}
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-600 line-clamp-2">{s.bio || "—"}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {s.service_ids.slice(0, 3).map((sid) => servicesById[sid] && (
                  <span key={sid} className="text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-1 rounded">
                    {servicesById[sid].name}
                  </span>
                ))}
                {s.service_ids.length > 3 && <span className="text-[10px] text-slate-500">+{s.service_ids.length - 3}</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`staff-edit-${s.id}`}><Pencil className="h-3 w-3 mr-1" /> Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
